import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'

import { TextBox } from 'srcReact/components/common2/TextBox'
import PasswordTextBox from 'srcReact/components/common2/PasswordTextBox'
import Button from 'srcReact/components/common2/Button'
import Icon from 'srcReact/components/icons/Icon'
import { isEmail, isEmpty } from 'srcReact/helpers/validator'

import { settingsActions as sa } from '../../action'
import styles from './styles.module.scss'
import { isPasswordComplex } from 'srcReact/helpers/utils'
import { loginActions } from 'srcReact/LoginPage'
import ErrorMessage from 'srcReact/components/common2/ErrorMessage'
import DateOfBirth from 'srcReact/components/common2/DateOfBirth'
import Dropdown1 from 'srcReact/components/common2/Dropdown1'
import { formatDate } from 'srcReact/helpers/datetimeHelper'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import Spinner from 'srcReact/components/common2/Spinner'

export default function UpdateForm() {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const {
    userDetails,
    resetSuccess,
    codeSuccess,
    codeError,
    newEmailObj,
    newEmailConfirmationObj,
    emailActivationSuccess,
    emailActivationFailure,
    numberUpdateSuccess,
    numberUpdateFailure,
    passwordUpdateSuccess,
    passwordUpdateFailure,
    passwordResetSuccess,
    passwordResetFailure,
    credValue
  } = useSelector((state) => state.settings)

  const { email, phone_number, country, country_code, gender, birthday, loading } = userDetails

  const [genderOptions] = useState([
    { label: t('signup.information_module.woman'), value: 'woman' },
    { label: t('signup.information_module.man'), value: 'man' },
    { label: t('signup.information_module.nonconforming'), value: 'nonconforming' }
  ])

  const dialCodeLength = JSON.parse(localStorage.getItem('dialCodeLength'))

  const [newEmail, setNewEmail] = useState(newEmailObj)
  const [newEmailConfirmation, setNewEmailConfirmation] = useState(newEmailConfirmationObj)
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('')
  const [oldPwdError, setOldPwdError] = useState('')
  const [newPwdError, setNewPwdError] = useState('')
  const [confirmationError, setConfirmationError] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [userGender, setUserGender] = useState(genderOptions.find((x) => x.value === gender))
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (emailActivationFailure && !emailActivationFailure.success) {
      dispatch(sa.setCodeError(true))
      dispatch(sa.setInitial())
      setErrorMessage(emailActivationFailure)
    } else if (emailActivationSuccess && emailActivationSuccess.email) {
      dispatch(sa.setUpdatedEmail(newEmailConfirmation.value))
      dispatch(sa.setCodeError(false))
      dispatch(sa.setInitial())
      dispatch(sa.setVerificationFormActive(true))
    }
  }, [emailActivationSuccess, emailActivationFailure])

  useEffect(() => {
    if (passwordUpdateFailure && !passwordUpdateFailure.success) {
      dispatch(sa.setCodeError(true))
      dispatch(sa.setInitial())
      setErrorMessage(passwordUpdateFailure)
    } else if (passwordUpdateSuccess && passwordUpdateSuccess.userId) {
      dispatch(sa.setCodeError(false))
      dispatch(sa.setSuccess(true))
      // setSuccess(true);
      dispatch(sa.setInitial())
      setTimeout(() => {
        dispatch(loginActions.userLogout())
      }, 1000)
    }
  }, [passwordUpdateSuccess, passwordUpdateFailure])

  useEffect(() => {
    if (numberUpdateFailure && !numberUpdateFailure.success) {
      dispatch(sa.setCodeError(true))
      dispatch(sa.setInitial())
      setErrorMessage(numberUpdateFailure.message)
    } else if (numberUpdateSuccess && numberUpdateSuccess.phone_number) {
      dispatch(sa.setCodeError(false))
      dispatch(sa.setSuccess(true))
      // setSuccess(true);
      dispatch(sa.setInitial())
    }
  }, [numberUpdateSuccess, numberUpdateFailure])

  useEffect(() => {
    if (passwordResetFailure && !passwordResetFailure.success) {
      dispatch(sa.setResetSuccess(false))
      dispatch(sa.setCodeError(true))
      dispatch(sa.setInitial())
      setErrorMessage(passwordResetFailure.message)
    } else if (passwordResetSuccess) {
      dispatch(sa.setCodeError(false))
      dispatch(sa.setResetSuccess(true))
      dispatch(sa.setInitial())
    }
  }, [passwordResetSuccess, passwordResetFailure])

  const handlePassword = (e) => {
    setOldPassword(e.target.value)
  }

  const handleNewPassword = (e) => {
    setNewPassword(e.target.value)
  }

  const handleNewPasswordConfirmation = (e) => {
    setNewPasswordConfirmation(e.target.value)
  }

  const handleForgotPassword = () => {
    dispatch(sa.setPasswordResetKey())
  }

  const handleNumber = (number, selected, e, formattedValue) => {
    let phone_number = {
      value: formattedValue,
      country_code: `${'+' + selected.dialCode}`,
      country_abbr: selected.countryCode,
      country_name: selected.name
    }
    setPhoneNumber(phone_number)
  }

  const handlePasswordValidation = () => {
    validatePassword('old')
  }

  const handleNewPasswordValidation = () => {
    validatePassword('new')
  }

  const handleNewPasswordConfirmationValidation = () => {
    validatePassword('confirmation')
  }

  const regex = /^(?=.*[0-9])(?=.*[a-zA-Z])([a-zA-Z0-9]+)$/

  const validatePassword = (type) => {
    if (type === 'old') {
      if (isEmpty(oldPassword)) {
        setOldPwdError(t('account_settings.error_messages.password_req'))
        return false
      }
    }

    if (type === 'new') {
      if (isEmpty(newPassword)) {
        setNewPwdError(t('account_settings.error_messages.password_req'))
        return false
      }

      if (!isPasswordComplex(newPassword)) {
        setNewPwdError(t('global.passwordSuggestion'))
        return false
      }
    }

    if (type === 'confirmation') {
      if (!isPasswordComplex(newPassword)) {
        setNewPwdError(t('global.passwordSuggestion'))
        return false
      }
      if (newPassword !== newPasswordConfirmation) {
        setConfirmationError(t(t('account_settings.error_messages.not_same_values')))
        return false
      }
    }

    setOldPwdError('')
    setNewPwdError('')
    setConfirmationError('')
    return true
  }

  const handleEmail = (e) => {
    if (e && e.target) {
      setNewEmail({ ...newEmail, value: e.target.value })
    }
  }

  const handleEmailValidation = (e) => {
    if (e && e.target && e.target.value) {
      validateEmail()
    }
  }

  const validateEmail = () => {
    if (isEmpty(newEmail.value)) {
      setNewEmail({ ...newEmail, hasError: true, errorMessage: t('account_settings.error_messages.email_req') })
      return false
    }
    if (!isEmail(newEmail.value)) {
      setNewEmail({ ...newEmail, hasError: true, errorMessage: t('account_settings.error_messages.wrong_email') })
      return false
    }

    setNewEmail({ ...newEmail, hasError: false, errorMessage: '' })
    return true
  }

  const handleEmailConfirmation = (e) => {
    if (e && e.target) {
      setNewEmailConfirmation({ ...newEmailConfirmation, value: e.target.value })
    }
  }

  const handleConfirmationValidation = (e) => {
    if (e && e.target && e.target.value) {
      validateEmailConfirmation()
    }
  }

  const validateEmailConfirmation = () => {
    if (isEmpty(newEmailConfirmation.value)) {
      setNewEmailConfirmation({
        ...newEmailConfirmation,
        hasError: true,
        errorMessage: t('account_settings.error_messages.email_req')
      })
      return false
    }
    if (newEmail.value.toLowerCase() !== newEmailConfirmation.value.toLowerCase()) {
      setNewEmailConfirmation({
        ...newEmailConfirmation,
        hasError: true,
        errorMessage: t('account_settings.error_messages.not_same_values')
      })
      return false
    }

    setNewEmailConfirmation({ ...newEmailConfirmation, hasError: false, errorMessage: '' })
    return true
  }

  const handleClick = (credValue) => {
    if (credValue === 'email') {
      let payload = {
        email: newEmailConfirmation.value
      }
      dispatch(sa.setEmailActivationCode(payload))
    } else if (credValue === 'password') {
      let payload = {
        old_password: oldPassword,
        password: newPasswordConfirmation
      }
      dispatch(sa.putPassword(payload))
    } else if (credValue === 'number') {
      let payload = {
        phone_number: phoneNumber.value,
        country: phoneNumber.country_abbr,
        country_code: phoneNumber.country_code + ' ' + phoneNumber.country_name
      }
      dispatch(sa.putNumber(payload))
    } else if (credValue === 'dob') {
      dispatch(sa.putUserInfo('birthday', formatDate(dateOfBirth, appConstantSettings.DATE_FORMAT8)))
    } else if (credValue === 'gender') {
      dispatch(sa.putUserInfo('gender', userGender.value))
    }
  }

  const handleClose = () => {
    dispatch(sa.setUpdateFormActive(false))
  }

  const handleValidClick = () => {
    if (credValue === 'number' || credValue === 'dob' || credValue === 'gender') {
      handleClick(credValue)
    } else {
      let email = validateEmail()
      let emailConfirmation = validateEmailConfirmation()
      if (email && emailConfirmation) {
        handleClick(credValue)
        return
      }
      if (validatePassword('old') && validatePassword('new') && validatePassword('confirmation')) {
        handleClick(credValue)
        return
      }
    }
  }
  const getTitle = (type) => {
    if (type == 'email') {
      return t('account_settings.update_form.changeEmail')
    } else if (type === 'password') {
      return t('account_settings.update_form.changePassword')
    } else if (type === 'number') {
      return t('account_settings.update_form.changeNumber')
    } else if (type === 'dob') {
      return t('account_settings.update_form.changeDOB')
    } else if (type === 'gender') {
      return t('account_settings.update_form.changeGender')
    }
  }

  const hasSubtitle = (type) => {
    if (type === 'email' && email) {
      return true
    } else if (type === 'number' && phone_number) {
      return true
    }
    return false
  }

  const getSubTitle = (type) => {
    let title =
      type === 'email'
        ? t('account_settings.update_form.currentEmail')
        : type === 'number'
        ? t('account_settings.update_form.currentNumber')
        : ''
    let value = type === 'email' ? email : type === 'number' ? phone_number : ''

    return (
      <div className={`${styles.currentCred}`}>
        {title}
        <span>{value}</span>
      </div>
    )
  }

  const handleDOBChange = (day, month, year) => {
    const date = new Date(year, month - 1, day)
    setDateOfBirth(date)
  }

  return (
    <div className={`${styles.mainWrapper}`}>
      <div className={`${styles.boxWrapper}`}>
        <div className={`${styles.closeIcon}`} onClick={handleClose}>
          <Icon name='close' alt='' />
        </div>
        <header className={`${styles.header}`}>{getTitle(credValue)}</header>
        {hasSubtitle(credValue) && getSubTitle(credValue)}
        {codeSuccess ? (
          <div className={`${styles.successBlock}`}>
            <img src={config.assetUrl + '/images/check.png'} alt='' />
            <h2>
              {' '}
              {t('account_settings.verification_form.your') +
                credValue +
                t('account_settings.verification_form.success')}
            </h2>
          </div>
        ) : (
          <div className={`${styles.formWrapper}`}>
            {credValue === 'dob' ? (
              <DateOfBirth
                dayOfBirth={birthday?.split('-')[2]}
                monthOfBirth={{ value: parseInt(birthday?.split('-')[1]) }}
                yearOfBirth={birthday?.split('-')[0]}
                onChange={handleDOBChange}
              />
            ) : credValue === 'gender' ? (
              <Dropdown1
                className={styles.genderSelectedItem}
                options={genderOptions}
                placeholder={t('signup.information_module.gender')}
                value={userGender}
                onChange={(e) => {
                  setUserGender(e)
                }}
              />
            ) : credValue === 'number' ? (
              <React.Fragment>
                <label className={`${styles.pwdLabel}`}>{t('account_settings.update_form.phone')}</label>
                <PhoneInput
                  country={country}
                  countryCodeEditable={false}
                  placeholder={t('account_settings.update_form.phone_number')}
                  value={phoneNumber.value}
                  onChange={(number, selected, e, formattedValue) => handleNumber(number, selected, e, formattedValue)}
                  containerClass={`${styles.phoneInputContainer}`}
                  inputClass={`${styles.phoneInput}`}
                />
              </React.Fragment>
            ) : (
              <React.Fragment>
                {credValue === 'password' ? (
                  <React.Fragment>
                    <PasswordTextBox
                      label={t('account_settings.update_form.currentPswd')}
                      placeholder={t('account_settings.update_form.typeCurrentPassword')}
                      enableShowPassword={false}
                      value={oldPassword}
                      containerClassName={styles.pwdInput}
                      handleChange={handlePassword}
                      autoComplete='new-password'
                      onBlur={handlePasswordValidation}
                      direction='column'
                      isRequired={true}
                      errorMessage={oldPwdError}
                    />
                    <PasswordTextBox
                      label={t('account_settings.update_form.newPassword')}
                      placeholder={t('account_settings.update_form.passwordPlaceholder')}
                      value={newPassword}
                      containerClassName={styles.pwdInput}
                      handleChange={handleNewPassword}
                      onBlur={handleNewPasswordValidation}
                      direction='column'
                      isRequired={true}
                      errorMessage={newPwdError}
                    />
                    <PasswordTextBox
                      label={t('account_settings.update_form.retypePassword')}
                      placeholder={t('account_settings.update_form.retypePasswordPlaceholder')}
                      value={newPasswordConfirmation}
                      containerClassName={styles.pwdInput}
                      handleChange={handleNewPasswordConfirmation}
                      onBlur={handleNewPasswordConfirmationValidation}
                      direction='column'
                      isRequired={true}
                      errorMessage={confirmationError}
                    />
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    <TextBox
                      customWrapperCss={styles.textBox}
                      customLabelCss={styles.label}
                      fieldInfo={newEmail}
                      label={t('account_settings.update_form.newEmail')}
                      placeholder={t('account_settings.update_form.emailPlaceholder')}
                      onChange={handleEmail}
                      onBlur={handleEmailValidation}
                    />
                    <TextBox
                      customWrapperCss={styles.textBox}
                      customLabelCss={styles.label}
                      fieldInfo={newEmailConfirmation}
                      label={t('account_settings.update_form.retypeEmail')}
                      placeholder={t('account_settings.update_form.retypeEmailPlaceholder')}
                      onChange={handleEmailConfirmation}
                      onBlur={handleConfirmationValidation}
                    />
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
            {codeError && <ErrorMessage text={errorMessage} className={styles.errorText} />}
            <Button
              text={
                credValue === 'email'
                  ? t('account_settings.update_form.email_button_text')
                  : t('account_settings.update_form.number_button_text')
              }
              handleClick={handleValidClick}
              customStyle={styles.sendButton}
              size='large'
            />
            {credValue === 'password' ? (
              <div className={styles.forgot_password} onClick={handleForgotPassword}>
                {t('account_settings.update_form.forgot_password')}
              </div>
            ) : (
              ''
            )}
            {resetSuccess && (
              <label className={`${styles.successText}`}>{t('account_settings.update_form.reset_success')}</label>
            )}
            {loading && <Spinner />}
          </div>
        )}
      </div>
    </div>
  )
}
